<template>
  <div
    class="flex flex-col items-start page-wrapper md:flex-row h-full p-0 flex-grow"
  >
    <FamilyConfigurationNewCustomerFlowContent />

    <LazyPricingPriceDetailsModal
      v-if="priceDetails"
      v-model="showPriceModal"
      :price-details="priceDetails"
    />

    <StickyFooterContainer>
      <div class="max-w-screen-xl w-full flex flex-row md:gap-24 px-0">
        <div class="flex flex-col md:flex-row grow">
          <StickyFooterPriceDisplay
            :strike-through-price-exists-and-differs="
              strikeThroughPriceExistsAndDiffers
            "
            :current-total-regular-monthly-price-to-display="
              regularMonthlyPriceDisplay
            "
            :current-total-monthly-price-to-display="`${familyStore.summary_totalMontlyCost || '0'}`"
            :in-x-months-text="inXMonthsText"
            current-total-direct-price-to-display="0"
            :current-full-price-string-for-screen-reader="
              currentFullPriceStringForScreenReader
            "
            :is-binding-period-active="
              familyStore.inputs.bindingPeriodRadioValue !== '0'
            "
            :is-monthly-payment="true"
            :action-button="{
              enabled: true,
              text: 'Se prisdetaljer',
              iconName: showPriceModal ? 'chevron-down' : 'chevron-up',
              ariaExpanded: showPriceModal,
            }"
            :desktop-usps="footerUspItems"
            @button-click="showPriceModal = true"
          />
        </div>

        <AppButton
          :show-loading="toCheckoutIsClicked"
          @on-click="goToCheckout()"
        >
          <span class="hidden md:block">Lägg till och fortsätt</span>
          <span class="block md:hidden">Till kassan</span>
        </AppButton>
      </div>
    </StickyFooterContainer>
  </div>
</template>

<script lang="ts" setup>
import { useFamilyPriceDetailsModal } from '~/composables/pricing/useFamilyPriceDetailsModal';

const familyStore = useFamilyStore();
const { $api } = useNuxtApp();
const { pushAddToCartEvent } = useGoogleAnalyticsEvents();
const { priceDetails } = useFamilyPriceDetailsModal();

const toCheckoutIsClicked: Ref<boolean> = ref(false);
const showPriceModal = ref(false);

const strikeThroughPriceExistsAndDiffers = computed(() => {
  const hasParentDiscount =
    familyStore.shouldIncludeParentPlanInPricing &&
    familyStore.selectedParentPlanBundlePrice?.Plan &&
    familyStore.selectedParentPlanBundlePrice.Plan.RegularMonthlyFee >
      familyStore.selectedParentPlanBundlePrice.Plan.MonthlyFee;

  const hasFamilyDiscount =
    familyStore.regularPriceOfAddedFamilyConnections >
    familyStore.priceOfAddedFamilyConnections;

  return !!(hasParentDiscount || hasFamilyDiscount);
});

const regularMonthlyPriceDisplay = computed(() => {
  if (!strikeThroughPriceExistsAndDiffers.value) {
    return '';
  }

  let regularTotal = 0;

  regularTotal +=
    familyStore.selectedParentPlanBundlePrice?.Plan?.RegularMonthlyFee ||
    familyStore.selectedParentPlanBundlePrice?.Plan?.MonthlyFee ||
    0;

  regularTotal +=
    familyStore.regularPriceOfAddedFamilyConnections ||
    familyStore.priceOfAddedFamilyConnections;

  const returnString =
    regularTotal > 0 ? regularTotal.toLocaleString('sv-SE') : '0';

  return returnString;
});

const goToCheckout = async () => {
  familyStore.ui.isBuyButtonLoading = true;

  try {
    await $api.ShoppingCartModule.AddBundleToCart(
      familyStore.requestPayloadForAddToCart,
    );
    pushAddToCartEvent(familyStore.trackingData);
    navigateTo('/shop/varukorg', { external: true });
  } catch (e) {
    familyStore.ui.generalErrorMessage =
      'Kunde inte lägga till abonnemangen till varukorgen just nu. Vänligen försök igen.';
    if (isNuxtError(e)) {
      logError(e);
    }
  } finally {
    familyStore.ui.isBuyButtonLoading = false;
  }
};

const currentFullPriceStringForScreenReader = computed(() => {
  const inNumberOfMonthsString = '';

  return `${familyStore.summary_totalMontlyCost} kronor i månaden${inNumberOfMonthsString}. 0 kronor att betala nu.`;
});

const inXMonthsText = computed(() => {
  return '';
});

const footerUspItems = computed(() => {
  return [
    {
      Icon: 'world',
      Label: 'Telias nät',
    },
    {
      Icon: 'message-square',
      Label: 'Fria samtal och sms',
    },
    {
      Icon: 'refresh',
      Label: 'Behåll oanvänd surf',
    },
  ];
});
</script>
